import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { bindActionCreators } from 'redux';

import { BaseApplicationState } from '@common/react/store';
import { BaseUserWithAvatar } from '@common/typescript/objects/BaseUser';
import { ChatMessage, EmojiReaction } from '@common/react/components/Chat/Chat';
import ChatReaction from '@common/react/components/Chat/Reactions/ChatReaction';
import ChatReactionButton from '@common/react/components/Chat/Reactions/ChatReactionButton';
import useRequest from '@common/react/hooks/useRequest';
import { useChatSettingsProviderContext } from '@common/react/components/Chat/ChatSettingsProvider';

interface ChatReactionComponentProps {
	message: ChatMessage;
}

const ChatReactionComponent: React.FC<ChatReactionComponentProps> = ({ message }) => {
	const request = useRequest();

	const { state: { requests, storageName, chatStoreSettings: { getActionCreators, getCurrentChat } } } = useChatSettingsProviderContext();
	const currentUser = useSelector((state: BaseApplicationState<BaseUserWithAvatar>) => state.login.user);

	const dispatch = useDispatch();
	const { addReaction, removeReaction } = React.useMemo(() => bindActionCreators(getActionCreators(), dispatch), []);

	const [isOpen, setIsOpen] = React.useState<boolean>(() => false);

	const handleReactionClick = (id: number) => {
		requests.chatEmojiReactionMessage
		&& request<EmojiReaction>(requests.chatEmojiReactionMessage, { id, deleted: true });
		removeReaction(message.chatId, message.id, id, storageName);
	};

	const handleReactionSelect = (emoji) => {
		requests.chatEmojiReactionMessage
		&& request<EmojiReaction>(requests.chatEmojiReactionMessage, { objectId: message.id, reaction: emoji.native, deleted: false })
			.then((emojiReaction) => addReaction(message.chatId, message.id, { ...emojiReaction, animate: true }, storageName));
		setIsOpen(false);
	};

	const handleReactionOpenChange = (isOpen: boolean) => {
		setIsOpen(isOpen);
	};

	const updateReaction = (reaction) => {
		addReaction(message.chatId, message.id, reaction);
	};

	return (
		currentUser
		&& <div className="chat-message-reactions">
			{
				message.emojiReactions && message.emojiReactions.map((emojiReaction) =>
					<ChatReaction
						updateReaction={updateReaction}
						key={`reaction-${emojiReaction.id}`}
						emojiReaction={emojiReaction}
						userId={currentUser.id}
						username={emojiReaction.user && emojiReaction.userId !== currentUser.id
							? `${emojiReaction.user.firstName} ${emojiReaction.user.lastName}`
							: 'Me'}
						handleReactionClick={handleReactionClick}
					/>)
			}
			{
				currentUser.id !== message.userId
				&& (message.emojiReactions || [])?.map((emojiReaction) => emojiReaction.userId).indexOf(currentUser.id) === -1
				&& <ChatReactionButton
					isOpen={isOpen}
					getPopupContainer={(node) => node.closest('.chat-message-list-component__item')}
					handleReactionSelect={handleReactionSelect}
					handleReactionOpenChange={handleReactionOpenChange}
				/>
			}
		</div>
	);
};

export default ChatReactionComponent;
